import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, CreditCard, Smartphone, Banknote, Loader2, ShieldCheck, RotateCcw, BadgeCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import CheckoutSteps from "../components/CheckoutSteps";
import CardPreview from "../components/CardPreview";

function formatINR(n) {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function formatCardNumber(value) {
  const digits = value.replace(/\D/g, "").slice(0, 16);
  return digits.match(/.{1,4}/g)?.join(" ") || digits;
}

function formatExpiry(value) {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

const PAYMENT_METHODS = [
  { id: "card", label: "Credit / Debit Card", icon: CreditCard },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "cod", label: "Cash on Delivery", icon: Banknote },
];

export function CheckoutPage() {
  const navigate = useNavigate();
  const { checkoutItems, clearCart, removeCheckoutFromCart } = useCart();
  const [method, setMethod] = useState("card");
  const [placing, setPlacing] = useState(false);
  const [cvvFocused, setCvvFocused] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    upiId: "",
  });

  useEffect(() => {
    if (!checkoutItems || checkoutItems.length === 0) {
      navigate("/cart", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const subtotal = checkoutItems.reduce(
    (sum, it) => sum + parseFloat(String(it.price).replace(/,/g, "")) * it.qty,
    0
  );
  const total = subtotal;

  function handlePlaceOrder(e) {
    e.preventDefault();
    setPlacing(true);
    const orderId = "MRQ" + Date.now().toString().slice(-8);
    setTimeout(() => {
      if (removeCheckoutFromCart) removeCheckoutFromCart();
      else clearCart();
      navigate("/order-success", {
        state: {
          orderId,
          total,
          items: checkoutItems,
          name: form.name,
          method,
        },
      });
    }, 1500);
  }

  if (!checkoutItems || checkoutItems.length === 0) return null;

  return (
    <section className="pt-32 pb-24 px-6 md:px-10 min-h-screen" style={{ background: "#0B0D10" }}>
      <div className="max-w-6xl mx-auto">
        <CheckoutSteps current="checkout" />

        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="uppercase tracking-widest text-xs mb-2" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#FF8A00" }}>
              Secure Checkout
            </p>
            <h1 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-4xl md:text-5xl">
              Checkout
            </h1>
          </div>
          <Link
            to="/cart"
            className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-widest"
            style={{ color: "rgba(245,241,234,0.6)" }}
          >
            <ArrowLeft size={15} /> Back to Bag
          </Link>
        </div>

        <form onSubmit={handlePlaceOrder} className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-2xl p-6" style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl mb-5">
                Contact Information
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="checkout-input"
                />
                <input
                  required
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="checkout-input"
                />
                <input
                  required
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="checkout-input sm:col-span-2"
                />
              </div>
            </div>

            <div className="rounded-2xl p-6" style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl mb-5">
                Shipping Address
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required
                  placeholder="Address line"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className="checkout-input sm:col-span-2"
                />
                <input
                  required
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className="checkout-input"
                />
                <input
                  required
                  placeholder="State"
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                  className="checkout-input"
                />
                <input
                  required
                  placeholder="Pincode"
                  value={form.pincode}
                  onChange={(e) => update("pincode", e.target.value)}
                  className="checkout-input"
                />
              </div>
            </div>

            <div className="rounded-2xl p-6" style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl mb-5">
                Payment Method
              </h2>
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className="flex flex-col items-center gap-2 py-4 rounded-xl text-xs uppercase tracking-widest"
                    style={{
                      background: method === m.id ? "rgba(30,154,255,0.12)" : "rgba(255,255,255,0.03)",
                      border: method === m.id ? "1px solid #1E9AFF" : "1px solid rgba(255,255,255,0.08)",
                      color: method === m.id ? "#1E9AFF" : "rgba(245,241,234,0.7)",
                    }}
                  >
                    <m.icon size={18} />
                    {m.label}
                  </button>
                ))}
              </div>

              {method === "card" && (
                <>
                  <CardPreview
                    number={form.cardNumber}
                    expiry={form.cardExpiry}
                    name={form.name}
                    cvv={form.cardCvv}
                    flipped={cvvFocused}
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      required
                      inputMode="numeric"
                      placeholder="Card number"
                      maxLength={19}
                      value={form.cardNumber}
                      onChange={(e) => update("cardNumber", formatCardNumber(e.target.value))}
                      className="checkout-input sm:col-span-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px" }}
                    />
                    <input
                      required
                      inputMode="numeric"
                      placeholder="MM/YY"
                      maxLength={5}
                      value={form.cardExpiry}
                      onChange={(e) => update("cardExpiry", formatExpiry(e.target.value))}
                      className="checkout-input"
                    />
                    <input
                      required
                      inputMode="numeric"
                      placeholder="CVV"
                      maxLength={3}
                      value={form.cardCvv}
                      onFocus={() => setCvvFocused(true)}
                      onBlur={() => setCvvFocused(false)}
                      onChange={(e) => update("cardCvv", e.target.value.replace(/\D/g, ""))}
                      className="checkout-input"
                    />
                  </div>
                </>
              )}

              {method === "upi" && (
                <input
                  required
                  placeholder="yourname@upi"
                  value={form.upiId}
                  onChange={(e) => update("upiId", e.target.value)}
                  className="checkout-input w-full"
                />
              )}

              {method === "cod" && (
                <p className="text-sm" style={{ color: "rgba(245,241,234,0.6)" }}>
                  Pay in cash when your order is delivered.
                </p>
              )}

              <div className="flex items-center gap-2 mt-5 text-xs" style={{ color: "rgba(245,241,234,0.4)" }}>
                <Lock size={12} /> This is a demo checkout — no real payment is processed.
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 sticky top-28">
            <div className="rounded-2xl p-6" style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <h2 style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-xl mb-6">
                Order Summary
              </h2>
              <div className="flex flex-col gap-4 mb-6 max-h-64 overflow-y-auto pr-1">
                {checkoutItems.map((item) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full" style={{ objectFit: "cover" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-sm truncate">
                        {item.name}
                      </p>
                      <p className="text-xs" style={{ color: "rgba(245,241,234,0.5)" }}>
                        Qty {item.qty}
                      </p>
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }} className="text-xs flex-shrink-0">
                      ₹{formatINR(parseFloat(String(item.price).replace(/,/g, "")) * item.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-3 text-sm mb-6" style={{ color: "rgba(245,241,234,0.7)" }}>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>₹{formatINR(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#3FA9FF" }}>Free</span>
                </div>
              </div>
              <div className="flex justify-between items-center pt-4 mb-6" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <span style={{ fontFamily: "Fraunces, serif", color: "#F5F1EA" }} className="text-lg">Total</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#F5F1EA" }} className="text-lg">₹{formatINR(total)}</span>
              </div>
              <button
                type="submit"
                disabled={placing}
                className="btn-primary w-full py-3.5 rounded-full text-sm tracking-widest uppercase flex items-center justify-center gap-2"
                style={{ opacity: placing ? 0.75 : 1 }}
              >
                {placing ? (
                  <>
                    <Loader2 size={16} className="spin" /> Placing Order...
                  </>
                ) : (
                  <>Place Order</>
                )}
              </button>
            </div>

            <div
              className="rounded-2xl p-5 flex flex-col gap-4"
              style={{ background: "#171B22", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="flex items-center gap-3">
                <ShieldCheck size={16} color="#1E9AFF" className="flex-shrink-0" />
                <span className="text-xs" style={{ color: "rgba(245,241,234,0.6)" }}>256-bit encrypted &amp; secure checkout</span>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw size={16} color="#1E9AFF" className="flex-shrink-0" />
                <span className="text-xs" style={{ color: "rgba(245,241,234,0.6)" }}>30-day easy returns on every order</span>
              </div>
              <div className="flex items-center gap-3">
                <BadgeCheck size={16} color="#1E9AFF" className="flex-shrink-0" />
                <span className="text-xs" style={{ color: "rgba(245,241,234,0.6)" }}>100% authenticity guaranteed</span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

export default CheckoutPage;
